import {effect, Injectable, signal} from "@angular/core";
import {Theme} from "@app/core/services/theme";

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'fluxtail-theme';

  // Signal for current theme setting
  theme = signal<Theme>(this.getStoredTheme());

  // Signal for actual applied theme (resolves 'system' to light/dark)
  appliedTheme = signal<'light' | 'dark'>('dark');

  constructor() {
    // Apply theme on initialization
    this.applyTheme();

    // Watch for theme changes
    effect(() => {
      const theme = this.theme();
      this.applyTheme();
      localStorage.setItem(this.THEME_KEY, theme);
    });

    // Listen to system theme changes
    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.theme() === 'system') {
          this.applyTheme();
        }
      });
    }
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  private getStoredTheme(): Theme {
    if (typeof window === 'undefined') return 'dark';
    const stored = localStorage.getItem(this.THEME_KEY);
    return (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'dark';
  }

  private applyTheme(): void {
    if (typeof window === 'undefined') return;

    const theme = this.theme();
    let resolvedTheme: 'light' | 'dark' = 'dark';

    if (theme === 'system') {
      resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      resolvedTheme = theme;
    }

    this.appliedTheme.set(resolvedTheme);

    const themeValue = resolvedTheme === 'light' ? 'fluxtail-light' : 'fluxtail-dark';
    document.documentElement.setAttribute('data-theme', themeValue);
  }
}
